/* eslint-disable */
import React from 'react';
import PropTypes from 'prop-types';

import mergeClassName from 'merge-class-names';

import { createUseStyles, createUseColoredStyles } from '../../palette';
import decorate from '../../common/utils/decorate';
import useId from '../../common/hooks/useId';
import DeliveryWidgetContext from '../../contexts/DeliveryWidgetContext';

const useMainStyles = createUseStyles((theme) => ({
  container: {
    display: 'inline-block'
  },

  textInput: {
    ...theme.standarts('textInput'),

    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'flex-end',
    flexGrow: 1,

    '& > label': {
      ...theme.standarts('textInput'),

      fontSize: (props) => theme.getFactor('textInput', 'font.size', 0.714),

      marginBottom: theme.getFactor('textInput', 'indent', 0.25)
    },

    '& > div': {
      backgroundColor: theme.getColor('textInput', 'basic', 'value'),

      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      borderStyle: theme.get('textInput', 'border.style'),
      borderWidth: theme.get('textInput', 'border.width'),
      borderColor: theme.getColor('textInput', 'basic', 'border'),
      borderRadius: theme.get('textInput', 'border.radius')
    },

    '& > div:hover': {
      backgroundColor: theme.getColor('textInput', 'hover', 'value')
    },

    '& > div > input, & > div > textarea': {
      ...theme.standarts('textInput'),

      backgroundColor: 'transparent',
      fontSize: (props) => theme.getFactor('textInput', 'font.size', 0.857),

      boxSizing: 'border-box',
      border: 'none',
      outline: 'none',
      padding: theme.getFactor('textInput', 'indent', 0.5),

      width: '100%'
    },

    '& > span': {
      ...theme.standarts('textInput'),

      fontSize: (props) => theme.getFactor('textInput', 'font.size', 0.57),
      //color: theme.getColor('textInput', 'disabled', 'value'),

      marginTop: theme.getFactor('textInput', 'indent', 0.25)
    }
  },

  error: {
    '& > div': {
      borderColor: theme.getColor('textInput', 'danger', 'value')
    },

    '& > div > input, & > div > textarea': {
      color: theme.getColor('textInput', 'danger', 'value')
    },

    '& > span': {
      color: theme.getColor('textInput', 'danger', 'value')
    }
  },

  focused: {
    '& > div': {
      borderColor: theme.getColor(
        'textInput',
        'primary',
        'value',
        null,
        null,
        null,
        0.6
      )
    }
  }
}));

const useVariantStylesMap = createUseColoredStyles((theme, color) => ({
  normal: {
    '& > label': {
      color: theme.getColor('text', color, 'contrast')
    }
  }
}));

export default decorate(
  class extends React.PureComponent {
    static contextType = DeliveryWidgetContext;

    static decorateForwardRef = 'forwardRef';

    static decorateWith = {
      id: (props) => props.id || 'text' + useId() + 'input',
      classes: {
        main: useMainStyles,
        variant: useVariantStylesMap
      }
    };

    static propTypes = {
      radio: PropTypes.bool,
      variant: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired
    };

    static defaultProps = {
      variant: 'normal',
      color: 'basic'
    };

    constructor(props) {
      super(props);

      this.state = { isFocused: false };
    }

    onInputFocus = (event) => {
      this.setState((state) => ({ ...state, isFocused: true }));

      return typeof this.props.onFocus === 'function'
        ? this.props.onFocus(event)
        : undefined;
    };

    onInputBlur = (event) => {
      this.setState((state) => ({ ...state, isFocused: false }));

      return typeof this.props.onBlur === 'function'
        ? this.props.onBlur(event)
        : undefined;
    };

    langString = (value) => {
      const context = this.context;

      if (!context) return value;

      return context.langString(value);
    };

    componentDidMount() {}

    componentWillUnmount() {}

    componentDidUpdate(prevProps, prevState, snapshot) {}

    render() {
      const {
        forwardRef,

        classes,
        variant,
        color,

        id,
        multiline,
        required,
        label,
        note,
        defaultNote,
        startAdornment,
        endAdornment,
        isError,

        ...props
      } = this.props;

      delete props.onFocus;
      delete props.onBlur;

      const mergedClassName = mergeClassName(
        classes.main.textInput,
        multiline && classes.main.multiline,
        required && classes.main.required,
        isError && classes.main.error,
        this.state.isFocused && classes.main.focused,
        (classes.variant[color] ?? classes.variant.basic)[variant]
      );

      return (
        <div className={mergedClassName}>
          {label ? <label htmlFor={id}>{label}</label> : null}

          <div>
            {startAdornment ? <div>{startAdornment}</div> : null}

            {multiline ? (
              <textarea
                ref={forwardRef}
                id={id}
                {...props}
                onFocus={this.onInputFocus}
                onBlur={this.onInputBlur}
              />
            ) : (
              <input
                ref={forwardRef}
                type="text"
                id={id}
                {...props}
                onFocus={this.onInputFocus}
                onBlur={this.onInputBlur}
              />
            )}

            {endAdornment ? <div>{endAdornment}</div> : null}
          </div>

          {note || required || defaultNote ? (
            <span>
              {note ||
                (required && this.langString('input.required')) ||
                defaultNote}
            </span>
          ) : null}
        </div>
      );
    }
  }
);
